import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import api, {
  clearPersistedOrganizationScopeForUser,
  getOrganizationScopeStorageKey,
  setApiOrganizationScope,
  setOrganizationScopeErrorHandler,
  withoutOrganizationScope,
} from "../lib/api";

export const PLATFORM_ADMIN_ROLE = "platform_admin";

// Any well-formed GUID, deliberately without the RFC 4122 version/variant nibble checks:
// organization IDs are whatever the backend stores, and not all of them are v4. The seeded
// demo organization is 00000001-0000-0000-0000-000000000001, which a version-aware pattern
// rejects — that silently sent every attempt to select it into clearScope(). The real
// validation is the membership check against the loaded organization list below (and the
// backend's own 404), so this only has to reject obvious garbage from localStorage.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const OrganizationScopeContext = createContext(null);

export const isPlatformAdmin = (user) => user?.role === PLATFORM_ADMIN_ROLE;

const readPersistedOrganizationScope = (userId) => {
  if (!userId) return null;

  try {
    const value = window.localStorage.getItem(getOrganizationScopeStorageKey(userId));
    return value && UUID_PATTERN.test(value) ? value : null;
  } catch (error) {
    console.warn("Unable to read the persisted organization scope:", error);
    return null;
  }
};

const persistOrganizationScope = (userId, organizationId) => {
  if (!userId || !UUID_PATTERN.test(organizationId)) return;

  try {
    window.localStorage.setItem(getOrganizationScopeStorageKey(userId), organizationId);
  } catch (error) {
    console.warn("Unable to persist the organization scope:", error);
  }
};

export const useOrganizationScope = () => {
  const context = useContext(OrganizationScopeContext);
  if (!context) {
    throw new Error("useOrganizationScope must be used within OrganizationScopeProvider");
  }
  return context;
};

export const OrganizationScopeProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [activeOrganizationId, setActiveOrganizationId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsError, setOrganizationsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scopeVersion, setScopeVersion] = useState(0);
  const activeOrganizationIdRef = useRef(null);
  const scopeChangeGuardsRef = useRef(new Set());
  const userRef = useRef(user);
  const refreshOrganizationsRef = useRef(null);

  userRef.current = user;

  const applyScope = useCallback((organizationId, { persist = true } = {}) => {
    const normalizedId = organizationId || null;
    if (activeOrganizationIdRef.current === normalizedId) return;

    setApiOrganizationScope(normalizedId);
    activeOrganizationIdRef.current = normalizedId;

    if (persist) {
      if (normalizedId) {
        persistOrganizationScope(userRef.current?.id, normalizedId);
      } else {
        clearPersistedOrganizationScopeForUser(userRef.current?.id);
      }
    }

    setActiveOrganizationId(normalizedId);
    setScopeVersion((version) => version + 1);
  }, []);

  const canChangeScope = useCallback(() => (
    [...scopeChangeGuardsRef.current].every((guard) => guard() !== false)
  ), []);

  const registerScopeChangeGuard = useCallback((guard) => {
    scopeChangeGuardsRef.current.add(guard);
    return () => scopeChangeGuardsRef.current.delete(guard);
  }, []);

  const clearScope = useCallback(() => {
    if (activeOrganizationIdRef.current && !canChangeScope()) return false;
    applyScope(null);
    return true;
  }, [applyScope, canChangeScope]);

  const setActiveOrganization = useCallback((organizationId) => {
    if (!organizationId) {
      clearScope();
      return;
    }

    if (!UUID_PATTERN.test(organizationId) || !organizations.some((org) => org.id === organizationId)) {
      clearScope();
      return false;
    }

    if (activeOrganizationIdRef.current !== organizationId && !canChangeScope()) return false;
    applyScope(organizationId);
    return true;
  }, [applyScope, canChangeScope, clearScope, organizations]);

  const refreshOrganizations = useCallback(async () => {
    if (!isPlatformAdmin(userRef.current)) {
      setOrganizations([]);
      setOrganizationsError(false);
      return [];
    }

    try {
      const response = await api.get("/organizations", withoutOrganizationScope());
      const nextOrganizations = response.data || [];
      setOrganizations(nextOrganizations);
      setOrganizationsError(false);

      const currentId = activeOrganizationIdRef.current;
      if (currentId && !nextOrganizations.some((org) => org.id === currentId)) {
        applyScope(null);
      }

      return nextOrganizations;
    } catch (error) {
      setOrganizationsError(true);
      throw error;
    }
  }, [applyScope]);

  refreshOrganizationsRef.current = refreshOrganizations;

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      setApiOrganizationScope(null);
      activeOrganizationIdRef.current = null;
      setActiveOrganizationId(null);

      if (authLoading) return;

      if (!isPlatformAdmin(user)) {
        setOrganizations([]);
        setOrganizationsError(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get("/organizations", withoutOrganizationScope());
        if (cancelled) return;

        const nextOrganizations = response.data || [];
        const persistedId = readPersistedOrganizationScope(user.id);
        const restoredId = nextOrganizations.some((org) => org.id === persistedId) ? persistedId : null;

        setOrganizations(nextOrganizations);
        setOrganizationsError(false);
        if (restoredId) {
          applyScope(restoredId, { persist: false });
        } else {
          clearPersistedOrganizationScopeForUser(user.id);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize organization scope:", error);
          setOrganizations([]);
          setOrganizationsError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initialize();
    return () => {
      cancelled = true;
    };
  }, [applyScope, authLoading, user]);

  useEffect(() => setOrganizationScopeErrorHandler((error) => {
    applyScope(null);
    if (error) {
      refreshOrganizationsRef.current?.().catch((refreshError) => {
        console.error("Failed to refresh organizations after a scope error:", refreshError);
      });
    }
  }), [applyScope]);

  const platformAdmin = isPlatformAdmin(user);
  const value = useMemo(() => ({
    activeOrganizationId,
    organizations,
    organizationsError,
    loading,
    scopeVersion,
    isPlatformAdmin: platformAdmin,
    isAllOrganizations: platformAdmin && !activeOrganizationId,
    canUseOrganizationScope: !platformAdmin || Boolean(activeOrganizationId),
    setActiveOrganization,
    clearScope,
    registerScopeChangeGuard,
    refreshOrganizations,
  }), [
    activeOrganizationId,
    organizations,
    organizationsError,
    loading,
    scopeVersion,
    platformAdmin,
    setActiveOrganization,
    clearScope,
    registerScopeChangeGuard,
    refreshOrganizations,
  ]);

  return (
    <OrganizationScopeContext.Provider value={value}>
      {children}
    </OrganizationScopeContext.Provider>
  );
};
